/**
 * Bonnummers. Regel 11: per kraam per evenementdag van 1 t/m 99, daarna
 * opnieuw. Twee bonnen met nummer 7 op één dag bij één kraam kan dus niet;
 * bij honderd bonnen komt nummer 7 een tweede keer voorbij en dat is de
 * bedoeling — de klant staat er dan al lang niet meer.
 */

export const BONNUMMER_MIN = 1;
export const BONNUMMER_MAX = 99;

export function isBonnummer(waarde: unknown): waarde is number {
  return (
    typeof waarde === 'number' &&
    Number.isInteger(waarde) &&
    waarde >= BONNUMMER_MIN &&
    waarde <= BONNUMMER_MAX
  );
}

/** Het volgende nummer in de reeks. `null` betekent: eerste bon van de dag. */
export function volgendBonnummer(vorige: number | null): number {
  if (vorige === null) return BONNUMMER_MIN;
  if (!isBonnummer(vorige)) {
    throw new Error(`ongeldig bonnummer: ${String(vorige)}`);
  }
  return vorige === BONNUMMER_MAX ? BONNUMMER_MIN : vorige + 1;
}

/**
 * De evenementdag waar een bon bij hoort, als YYYY-MM-DD in Europe/Amsterdam.
 * Regel 5: opslaan in UTC, tonen (en tellen) in Amsterdamse tijd. Een bon van
 * 00:30 hoort bij de nacht, dus bij de dag die dan net begonnen is; een aparte
 * "festivaldag loopt tot 06:00"-regel zit er bewust niet in — dat maakt de
 * dagstaat onverklaarbaar voor de boekhouder.
 */
export function evenementdag(tijdstip: Date, tijdzone = 'Europe/Amsterdam'): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tijdzone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(tijdstip);
}

/** Toont een bonnummer zoals het op de bon en op het scherm staat: 007, 042. */
export function toonBonnummer(nummer: number): string {
  if (!isBonnummer(nummer)) {
    throw new Error(`ongeldig bonnummer: ${String(nummer)}`);
  }
  return String(nummer).padStart(3, '0');
}

/**
 * Het aantal dagen dat een evenement duurt, beide dagen meegeteld.
 *
 * Dit staat hier en niet in een scherm omdat het rechtstreeks in het geld
 * doorwerkt: het aantal dagen maal het aantal kraamsets maal het dagtarief is
 * onze factuur aan de organisator.
 */
export function evenementdagen(startOp: string, eindOp: string): number {
  const patroon = /^\d{4}-\d{2}-\d{2}$/;
  if (!patroon.test(startOp) || !patroon.test(eindOp)) {
    throw new Error('Een evenementdatum hoort als 2026-07-04 te worden aangeleverd.');
  }
  const start = Date.parse(`${startOp}T12:00:00Z`);
  const eind = Date.parse(`${eindOp}T12:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(eind)) {
    throw new Error('Onleesbare evenementdatum.');
  }
  if (eind < start) {
    throw new Error('De laatste dag ligt vóór de eerste dag.');
  }
  // Middaguur aan beide kanten, dus zomertijd kan er geen halve dag afhalen.
  return Math.trunc((eind - start) / 86_400_000) + 1;
}
